import { Grid } from "@mui/material";
import { useWallets } from "api/wallets";
import { OLClearIcon } from "components/CustomIcons/OLClearIcon";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { SelectInput } from "components/FormControls/Select/Select";
import { useMessage } from "hooks/intl";
import { useManageInfiniteScroll } from "hooks/useManageInfiniteScroll";
import { memo, useState } from "react";
import { useStyles } from "../TierSetForm.styles";

interface Props {
  condition: {
    attribute: string;
    walletType?: string;
  };
  initialValue: {
    label: string;
    id: string;
  };
  index: number;
  tierSetConditions: { label: string; attribute: string }[];
  isAndOperatorVisible: boolean;
  remove: (index: number) => void;
  replace: (index: number, value: any) => void;
}

export const Condition = memo(
  ({
    condition,
    index,
    initialValue,
    tierSetConditions,
    isAndOperatorVisible,
    remove,
    replace,
  }: Props) => {
    const [walletTypeSearch, setWalletTypeSearch] = useState("");
    const classes = useStyles();
    const { message } = useMessage();
    const { useInfiniteWalletsList } = useWallets();
    const {
      data: infiniteWalletsList,
      size: walletsSize,
      setSize: setWalletsSize,
      isValidating,
    } = useInfiniteWalletsList({
      filters: walletTypeSearch
        ? [
            {
              condition: "like",
              query: "name",
              value: walletTypeSearch,
            },
            {
              condition: "eq",
              query: "active",
              value: true,
            },
          ]
        : [
            {
              condition: "eq",
              query: "active",
              value: true,
            },
          ],
      order: "asc",
      sortKey: "name",
    });

    const { infiniteData: walletsList, anyElementsToFetch: anyWalletsToFetch } =
      useManageInfiniteScroll(infiniteWalletsList || []);

    const isWalletInputVisible =
      condition.attribute === "activeUnits" ||
      condition.attribute === "totalEarnedUnits" ||
      condition.attribute === "cumulatedEarnedUnits";

    return isWalletInputVisible ? (
      <>
        <Grid container columnSpacing={12}>
          <Grid item xs>
            <SelectInput
              label={<RequiredFieldLabel text={message({ id: "condition" })} />}
              name={`conditions.${index}.attribute`}
              initialValue={initialValue}
              items={tierSetConditions.map((condition) => ({
                id: condition.attribute,
                label: condition.label,
              }))}
              onChange={(e) => {
                const newInputValue = e.target.value;
                const selectedItem = tierSetConditions.find(
                  (condition) => condition.attribute === newInputValue,
                );

                if (selectedItem) {
                  const newValue = {
                    attribute: selectedItem.attribute,
                  };

                  replace(index, newValue);
                }
              }}
              data-test-id="condition"
            />
          </Grid>
          <Grid item xs>
            <SelectInput
              displaySearch
              data-test-id="wallet"
              width="100%"
              items={walletsList.map((wallet) => ({
                label: wallet.name,
                id: wallet.code,
              }))}
              label={<RequiredFieldLabel text={message({ id: "wallet" })} />}
              name={`conditions.${index}.walletType`}
              placeholder={message({
                id: "wallet",
              })}
              asyncSearch={(searchedQuery) =>
                setWalletTypeSearch(searchedQuery)
              }
              initialValue={{
                id: condition?.walletType as string,
                label: walletsList.find(
                  (wallet) => wallet.code === condition?.walletType,
                )?.name as string,
              }}
              loadMoreElements={() =>
                !isValidating && setWalletsSize(walletsSize + 1)
              }
              anyElementsToFetch={anyWalletsToFetch}
            />
          </Grid>
          <Grid xs="auto" item alignSelf="center">
            <div className={classes.clearIconWidth}>
              <OLClearIcon data-test-id="clear" onClick={() => remove(index)} />
            </div>
          </Grid>
          {isAndOperatorVisible && (
            <Grid item xs={12} className={classes.andOperator}>
              {message({
                id: "and",
              }).toUpperCase()}
            </Grid>
          )}
        </Grid>
      </>
    ) : (
      <>
        <Grid key={index} container columnSpacing={12}>
          <Grid item xs>
            <SelectInput
              label={<RequiredFieldLabel text={message({ id: "condition" })} />}
              name={`conditions.${index}.attribute`}
              initialValue={initialValue}
              items={tierSetConditions.map((condition) => ({
                id: condition.attribute,
                label: message({ id: condition.label }),
              }))}
              onChange={(e) => {
                const newInputValue = e.target.value;
                const selectedItem = tierSetConditions.find(
                  (condition) => condition.attribute === newInputValue,
                );

                if (selectedItem) {
                  const newValue = {
                    attribute: selectedItem.attribute,
                  };

                  replace(index, newValue);
                }
              }}
              data-test-id="condition"
            />
          </Grid>
          <Grid xs="auto" item alignSelf="center">
            <div className={classes.clearIconWidth}>
              <OLClearIcon data-test-id="clear" onClick={() => remove(index)} />
            </div>
          </Grid>
          {isAndOperatorVisible && (
            <Grid item xs={12} className={classes.andOperator}>
              {message({
                id: "and",
              }).toUpperCase()}
            </Grid>
          )}
        </Grid>
      </>
    );
  },
);
